import type { Notification } from '@notify-x/shared';
import type { ApiClient } from '../services/api-client.js';
import type { StorageService } from '../services/storage.js';
import { attachNotificationClickHandlers, renderNotificationItem } from './notification-item.js';

export class NotificationList {
	private notifications: Notification[] = [];
	private loading = false;
	private hasMore = false;
	private offset = 0;
	private readonly limit = 50;

	constructor(
		private apiClient: ApiClient,
		private storageService: StorageService,
	) {}

	/**
	 * Render the notification list
	 */
	async render(container: HTMLElement): Promise<void> {
		container.innerHTML = `
			<div class="notifications">
				<div class="notifications-header">
					<h2>Notifications</h2>
					<div class="notifications-actions">
						<button id="refresh-button" class="button-secondary">🔄 Refresh</button>
						<button id="logout-button" class="button-secondary">🚪 Logout</button>
					</div>
				</div>

				<div id="notifications-list" class="notifications-list">
					<div class="loading">Loading notifications...</div>
				</div>

				<div id="load-more-container" class="load-more-container hidden">
					<button id="load-more-button" class="button-secondary">Load more</button>
				</div>
			</div>
		`;

		this.attachEventListeners(container);
		await this.loadNotifications();
	}

	/**
	 * Attach event listeners
	 */
	private attachEventListeners(container: HTMLElement): void {
		// Refresh button
		const refreshButton = container.querySelector('#refresh-button');
		refreshButton?.addEventListener('click', () => this.handleRefresh());

		// Logout button
		const logoutButton = container.querySelector('#logout-button');
		logoutButton?.addEventListener('click', () => this.handleLogout());

		// Load more button
		const loadMoreButton = container.querySelector('#load-more-button');
		loadMoreButton?.addEventListener('click', () => this.loadMore());
	}

	/**
	 * Load notifications from API
	 */
	private async loadNotifications(append = false): Promise<void> {
		if (this.loading) return;

		this.loading = true;
		const userId = this.storageService.getUserId();

		if (!userId) {
			this.showError('User not found. Please register again.');
			return;
		}

		try {
			const response = await this.apiClient.getNotifications({
				user_id: userId,
				limit: this.limit,
				offset: append ? this.offset : 0,
			});

			if (append) {
				this.notifications = [...this.notifications, ...response.notifications];
			} else {
				this.notifications = response.notifications;
				this.offset = 0;
			}

			this.hasMore = response.has_more;
			this.offset = this.notifications.length;

			this.renderNotifications();
		} catch (error) {
			console.error('Failed to load notifications:', error);
			this.showError('Failed to load notifications. Please try again.');
		} finally {
			this.loading = false;
		}
	}

	/**
	 * Render notifications in the list
	 */
	private renderNotifications(): void {
		const listContainer = document.getElementById('notifications-list');
		if (!listContainer) return;

		if (this.notifications.length === 0) {
			listContainer.innerHTML = '<div class="empty-state">📭 No notifications yet</div>';
			this.hideLoadMore();
			return;
		}

		// Render all notifications
		listContainer.innerHTML = this.notifications
			.map((notification) => renderNotificationItem(notification, () => {}))
			.join('');

		// Show/hide load more button
		if (this.hasMore) {
			this.showLoadMore();
		} else {
			this.hideLoadMore();
		}
	}

	/**
	 * Handle refresh button click
	 */
	private async handleRefresh(): Promise<void> {
		this.offset = 0;
		await this.loadNotifications();
	}

	/**
	 * Handle logout button click
	 */
	private handleLogout(): void {
		if (confirm('Are you sure you want to logout?')) {
			this.storageService.clear();
			window.location.reload();
		}
	}

	/**
	 * Load more notifications
	 */
	private async loadMore(): Promise<void> {
		await this.loadNotifications(true);
	}

	/**
	 * Show error message
	 */
	private showError(message: string): void {
		const listContainer = document.getElementById('notifications-list');
		if (listContainer) {
			listContainer.innerHTML = `<div class="error-state">⚠️ ${message}</div>`;
		}
	}

	/**
	 * Show load more button
	 */
	private showLoadMore(): void {
		const container = document.getElementById('load-more-container');
		container?.classList.remove('hidden');
	}

	/**
	 * Hide load more button
	 */
	private hideLoadMore(): void {
		const container = document.getElementById('load-more-container');
		container?.classList.add('hidden');
	}
}
